import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getJournalEntryById, updateJournalEntry, uploadAudioForEntry, ACCEPT_AUDIO_TYPES_STRING } from '../../services/bennyJournalService';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  Paper
} from '@mui/material';

const EditJournalEntry = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const { currentUser, firestoreUser } = useAuth();

  const [entry, setEntry] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [microsoftLink, setMicrosoftLink] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const audioInputRef = useRef(null);
  // Not handling file changes in this version for simplicity, but can be added.
  // const [file, setFile] = useState(null);
  // const [fileError, setFileError] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchEntryDetails = useCallback(async () => {
    if (!currentUser) {
      setError("You must be logged in to edit entries.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const entryData = await getJournalEntryById(entryId);
      if (!entryData) {
        setError('Journal entry not found.');
        setLoading(false);
        return;
      }
      // Authorization: Check if current user is admin or author
      if (!(firestoreUser?.roles?.admin || entryData.userId === currentUser.uid)) {
        setError('You are not authorized to edit this journal entry.');
        setLoading(false);
        return;
      }
      setEntry(entryData);
      setTitle(entryData.title);
      setDescription(entryData.description);
      setMicrosoftLink(entryData.microsoftLink || '');
      setError('');
    } catch (err) {
      console.error("Error fetching journal entry:", err);
      setError('Failed to load journal entry details. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [entryId, currentUser, firestoreUser]);

  useEffect(() => {
    fetchEntryDetails();
  }, [fetchEntryDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setError("Authentication required.");
      return;
    }
    if (!title.trim() || !description.trim()) {
      setError("Title and Description are required.");
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Construct the data to update, excluding file for now
      const updatedData = {
        title,
        description,
        microsoftLink: microsoftLink || null, // Ensure it's null if empty
      };

      await updateJournalEntry(entryId, updatedData, currentUser.uid);
      
      // If new audio file was selected, upload it
      if (audioFile) {
        await uploadAudioForEntry(entryId, audioFile);
      }
      
      setSuccess('Journal entry updated successfully!');
      // Optionally navigate away or refresh data
      setTimeout(() => navigate('/benny-journal'), 2000); 
    } catch (err) {
      console.error("Error updating journal entry:", err);
      setError('Failed to update journal entry. ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAudioFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.toLowerCase();
      if (!ext.endsWith('.mp3') && !ext.endsWith('.m4a') && !ext.endsWith('.wav')) {
        setAudioFile(null);
        if (audioInputRef.current) {
          audioInputRef.current.value = null;
        }
        setError('Invalid audio file type. Please upload MP3, M4A, or WAV.');
        return;
      }
      setAudioFile(selectedFile);
      setError('');
    } else {
      setAudioFile(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !entry) { // Show only error if entry couldn't be loaded at all
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/benny-journal')} sx={{ mt: 2 }}>Back to Journal</Button>
      </Container>
    );
  }
  
  // If there's an error but we have entry data, we show the form with the error message.
  // This handles authorization errors where the user can see the form but gets an error message.

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, backgroundColor: '#3c3c3c', color: 'white' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: '"Georgia", serif', color: '#d98d2a' }}>
          Edit Journal Entry
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
        
        {entry ? ( // Only render form if entry is loaded
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Title of the Chronicle"
                variant="outlined"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                InputLabelProps={{ style: { color: '#e0e0e0', fontFamily: '"Georgia", serif' } }}
                inputProps={{ style: { color: 'white', fontFamily: '"Georgia", serif' } }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#d98d2a' },
                    '&:hover fieldset': { borderColor: '#c67c1a' },
                    '&.Mui-focused fieldset': { borderColor: '#d98d2a' },
                  }
                }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Description / Tale"
                variant="outlined"
                fullWidth
                multiline
                rows={6}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                InputLabelProps={{ style: { color: '#e0e0e0', fontFamily: '"Georgia", serif' } }}
                inputProps={{ style: { color: 'white', fontFamily: '"Georgia", serif' } }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#d98d2a' },
                    '&:hover fieldset': { borderColor: '#c67c1a' },
                    '&.Mui-focused fieldset': { borderColor: '#d98d2a' },
                  }
                }}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Link to Microsoft Scroll (Optional)"
                variant="outlined"
                fullWidth
                value={microsoftLink}
                onChange={(e) => setMicrosoftLink(e.target.value)}
                InputLabelProps={{ style: { color: '#e0e0e0', fontFamily: '"Georgia", serif' } }}
                inputProps={{ style: { color: 'white', fontFamily: '"Georgia", serif' } }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#d98d2a' },
                    '&:hover fieldset': { borderColor: '#c67c1a' },
                    '&.Mui-focused fieldset': { borderColor: '#d98d2a' },
                  }
                }}
              />
            </Box>
            
            {/* Audio Upload Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: '#d98d2a', mb: 1, fontFamily: '"Georgia", serif' }}>
                {entry.audioURL ? 'Replace Audio Recording (Optional):' : 'Add Audio Recording (Optional):'}
              </Typography>
              {entry.audioURL && (
                <Typography variant="caption" sx={{ color: '#e0e0e0', mb: 1, display: 'block', fontFamily: '"Georgia", serif' }}>
                  Current: {entry.audioFileName || 'Audio file attached'}
                </Typography>
              )}
              <Button
                variant="outlined"
                component="label"
                sx={{
                  color: '#d98d2a',
                  borderColor: '#d98d2a',
                  fontFamily: '"Georgia", serif',
                  '&:hover': { 
                    borderColor: '#c67c1a', 
                    backgroundColor: 'rgba(217, 141, 42, 0.1)' 
                  },
                  mr: audioFile ? 1 : 0,
                }}
              >
                🎙️ Choose Audio
                <input
                  type="file"
                  hidden
                  accept={ACCEPT_AUDIO_TYPES_STRING}
                  onChange={handleAudioFileChange}
                  ref={audioInputRef}
                />
              </Button>
              {audioFile && (
                <Typography variant="body2" component="span" sx={{ color: '#F5F5F5', verticalAlign: 'middle', fontFamily: '"Georgia", serif' }}>
                  New: {audioFile.name}
                </Typography>
              )}
            </Box>
            
            {/* File input could be added here if needed */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                type="button" 
                variant="outlined" 
                onClick={() => navigate('/benny-journal')}
                disabled={saving}
                sx={{ 
                  borderColor: '#d98d2a', 
                  color: '#d98d2a', 
                  fontFamily: '"Georgia", serif',
                  '&:hover': { borderColor: '#c67c1a', backgroundColor: 'rgba(217, 141, 42, 0.1)'}
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={saving || !currentUser || !(firestoreUser?.roles?.admin || entry?.userId === currentUser.uid)} // Disable if not authorized
                sx={{ 
                  backgroundColor: '#d98d2a', 
                  color: '#333', 
                  fontFamily: '"Georgia", serif',
                  '&:hover': { backgroundColor: '#c67c1a' },
                  '&.Mui-disabled': { backgroundColor: '#555', color: '#888' }
                }}
              >
                {saving ? <CircularProgress size={24} sx={{color: '#333'}} /> : 'Save Changes'}
              </Button>
            </Box>
          </form>
        ) : (
          // This part is reached if loading is false, but entry is still null, and there was no initial blocking error.
          // This typically means an authorization error occurred after an initial successful load attempt or a non-blocking error.
          <Typography sx={{fontFamily: '"Georgia", serif'}}>Loading entry data or checking permissions...</Typography>
        )}
      </Paper>
    </Container>
  );
};

export default EditJournalEntry;
